import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import Vimeo from "@u-wave/react-vimeo";
import {
  Box,
  IconButton,
  Link as MuiLink,
  makeStyles,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import clsx from "clsx";
import { Trans, useTranslation } from "react-i18next";
import { ContentPageStyles } from "./styles";

const IMG = (name) => `/images/rascal-podcast/${encodeURIComponent(name)}`;

const SAMPLE_VIMEO_IDS_EN = [
  "1187386081",
  "1187386156",
  "1187386229",
  "1187386282",
];
const SAMPLE_VIMEO_IDS_FR = [
  "1189855378",
  "1189855380",
  "1189855379",
  "1189855377",
];

const HEADER_SOLID_AFTER = 40;

const useStyles = makeStyles((theme) => ({
  ...ContentPageStyles(theme),
  root: {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    backgroundColor: "#000",
    color: "#fff",
  },
  topNav: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 78,
    padding: theme.spacing(2, 2.5),
    backgroundColor: "transparent",
    borderBottom: "1px solid transparent",
    backdropFilter: "none",
    transition: "background-color 180ms ease, border-color 180ms ease",
  },
  topNavSolid: {
    backgroundColor: "#000",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    backdropFilter: "blur(10px)",
  },
  navBrand: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    gap: theme.spacing(1.25),
  },
  navLogo: {
    width: 44,
    height: 44,
    objectFit: "contain",
    display: "block",
    flexShrink: 0,
  },
  navTitle: {
    fontWeight: 700,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  navLinks: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  navLink: {
    color: "rgba(255,255,255,0.95)",
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  heroBleed: {
    position: "relative",
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  hero: {
    position: "relative",
    height: "100vh",
    minHeight: "100vh",
    overflow: "hidden",
    isolation: "isolate",
  },
  heroMedia: {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    overflow: "hidden",
  },
  heroBgImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    filter: "saturate(1.02) contrast(1.05)",
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.22) 55%, rgba(0,0,0,0.32) 100%)",
  },
  heroUi: {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    boxSizing: "border-box",
    padding: theme.spacing(2),
    paddingTop: 120,
    pointerEvents: "none",
  },
  heroUiCenter: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "auto",
  },
  heroContent: {
    textAlign: "center",
    padding: theme.spacing(1.25, 2),
    maxWidth: 1100,
    marginLeft: "auto",
    marginRight: "auto",
    width: "100%",
    minWidth: 0,
  },
  heroMark: {
    height: 280,
    width: "auto",
    maxWidth: "min(620px, 92vw)",
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
    filter: "drop-shadow(0 10px 26px rgba(0,0,0,0.55))",
  },
  heroSub: {
    marginTop: theme.spacing(1.25),
    fontStyle: "italic",
    color: "rgba(255,255,255,0.92)",
    fontSize: "clamp(1.05rem, 2.6vw, 1.4rem)",
  },
  heroCta: {
    marginTop: theme.spacing(1.25),
    display: "inline-block",
    fontWeight: 800,
    letterSpacing: "0.08em",
    fontSize: 26,
    textTransform: "uppercase",
    color: "#fff",
    textDecoration: "underline",
    fontStyle: "italic",
  },
  heroUiBottom: {
    flexShrink: 0,
    display: "flex",
    justifyContent: "center",
    paddingBottom: theme.spacing(2),
    pointerEvents: "auto",
  },
  heroScrollBtn: {
    color: "#fff",
    padding: theme.spacing(1.5),
    filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.4))",
    "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
    "& svg": { fontSize: 42 },
  },
  sectionBlue: {
    backgroundColor: "#007BFF",
    color: "#fff",
    padding: theme.spacing(3, 2),
    textAlign: "center",
  },
  section2Intro: {
    backgroundColor: "#007BFF",
    color: "#fff",
    padding: theme.spacing(7, 2),
    textAlign: "center",
  },
  section2IntroText: {
    maxWidth: 980,
    marginLeft: "auto",
    marginRight: "auto",
    fontSize: "clamp(0.98rem, 2vw, 1.15rem)",
    lineHeight: 1.55,
    opacity: 0.95,
  },
  section2Hero: {
    position: "relative",
    backgroundImage: "url(/images/rascal-podcast/2.png)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(8, 2),
    overflow: "hidden",
  },
  section2HeroOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(1200px 560px at 22% 48%, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.78) 100%)",
  },
  section2HeroInner: {
    position: "relative",
    zIndex: 1,
    maxWidth: 1100,
    marginLeft: "auto",
    marginRight: "auto",
  },
  section2Title: {
    fontWeight: 900,
    fontSize: "clamp(2.1rem, 4.4vw, 3.35rem)",
    marginBottom: theme.spacing(2),
    textShadow: "0 8px 22px rgba(0,0,0,0.55)",
  },
  section2List: {
    margin: 0,
    paddingLeft: theme.spacing(2.25),
    maxWidth: 760,
    "& li": {
      marginBottom: theme.spacing(1),
      fontSize: 18,
      lineHeight: 1.55,
      color: "rgba(255,255,255,0.92)",
      textShadow: "0 6px 18px rgba(0,0,0,0.6)",
    },
    "& li strong": {
      color: "#fff",
      fontWeight: 800,
    },
  },
  section2CtaRow: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(3.5),
  },
  section2Cta: {
    color: "#fff",
    fontWeight: 900,
    fontSize: 26,
    fontStyle: "italic",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    textDecoration: "underline",
    textUnderlineOffset: 6,
    textDecorationThickness: 2,
    filter: "drop-shadow(0 10px 22px rgba(0,0,0,0.7))",
    "&:hover": { opacity: 0.95 },
  },
  section3Band: {
    backgroundColor: "#007BFF",
    color: "#fff",
    padding: theme.spacing(8, 2),
    textAlign: "center",
    fontWeight: 800,
    fontStyle: "italic",
    fontSize: 26,
    letterSpacing: "0.01em",
  },
  section3: {
    backgroundColor: "#000",
    backgroundImage: "url(/images/Black.png)",
    backgroundRepeat: "repeat",
    color: "#fff",
    padding: theme.spacing(7, 2, 8),
  },
  section3Title: {
    textAlign: "center",
    fontWeight: 800,
    fontSize: "clamp(2.1rem, 4.4vw, 3.35rem)",
    marginBottom: theme.spacing(1.5),
  },
  section3Sub: {
    textAlign: "center",
    opacity: 0.92,
    marginBottom: theme.spacing(5),
    fontSize: 18,
    lineHeight: 1.55,
    maxWidth: 760,
    marginLeft: "auto",
    marginRight: "auto",
  },
  hostGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: theme.spacing(3),
    alignItems: "stretch",
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(2),
    },
  },
  hostCard: {
    position: "relative",
    gridColumn: "span 6",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      gridColumn: "span 12",
    },
  },
  hostImg: {
    width: "100%",
    height: 220,
    display: "block",
    [theme.breakpoints.up("md")]: {
      height: 260,
    },
  },

  hostText: {
    position: "absolute",
    left: theme.spacing(3),
    bottom: theme.spacing(2.5),
    zIndex: 1,
    fontSize: 19,
    color: "#fff",
  },
  hostLabel: {
    fontSize: 22,
    opacity: 0.9,
    lineHeight: 1.1,
  },
  hostName: {
    fontSize: 32,
    fontWeight: 900,
    lineHeight: 1.05,
    marginTop: theme.spacing(0.75),
    [theme.breakpoints.down("xs")]: {
      fontSize: 28,
    },
  },
  hostBody: {
    padding: theme.spacing(2.25, 3, 2.75),
    backgroundColor: "rgba(0,0,0,0.65)",
    borderTop: "1px solid rgba(255,255,255,0.06)",
  },
  hostBio: {
    marginTop: theme.spacing(1.75),
    fontSize: 19,
    color: "#fff",
    lineHeight: 1.55,
    "& em": { fontStyle: "italic" },
  },
  sectionDivider: {
    height: 1,
    backgroundColor: "rgba(255,255,255,0.28)",
    marginBottom: theme.spacing(7),
  },
  section4: {
    backgroundColor: "#000",
    backgroundImage: "url(/images/Black.png)",
    backgroundRepeat: "repeat",
    color: "#fff",
    padding: theme.spacing(0, 2, 8),
  },
  section4Title: {
    textAlign: "center",
    fontWeight: 800,
    fontSize: 40,
    marginBottom: theme.spacing(1.5),
    color: "#fff",
  },
  section4Sub: {
    textAlign: "center",
    fontSize: 18,
    lineHeight: 1.55,
    maxWidth: 760,
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: theme.spacing(5),
    color: "#fff",
    opacity: 0.92,
  },
  section4Role: {
    fontSize: 18,
    lineHeight: 1.15,
    opacity: 0.9,
    color: "#fff",
  },
  section4Name: {
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.15,
    color: "#fff",
  },
  section4Bio: {
    marginTop: theme.spacing(1.25),
    fontSize: 18,
    lineHeight: 1.55,
    color: "#fff",
  },
  section5: {
    backgroundColor: "#000",
    backgroundImage: "url(/images/Black.png)",
    backgroundRepeat: "repeat",
    color: "#fff",
    padding: theme.spacing(4, 2),
  },
  section5Inner: {
    padding: theme.spacing(6, 0),
  },
  section5Divider: {
    height: 1,
    backgroundColor: "rgba(255,255,255,0.38)",
    margin: theme.spacing(3, 0),
  },
  section6: {
    backgroundColor: "#000",
    backgroundImage: "url(/images/Black.png)",
    backgroundRepeat: "repeat",
    color: "#fff",
    padding: theme.spacing(0, 2, 6),
  },

  section6Inner: {
    padding: theme.spacing(5, 0, 4),
    textAlign: "center",
  },
  section6Text: {
    fontSize: 18,
    opacity: 0.92,
    color: "#fff",
    marginBottom: theme.spacing(3),
  },
  section6Cta: {
    color: "#fff",
    fontWeight: 900,
    fontSize: 26,
    fontStyle: "italic",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    textDecoration: "underline",
    textUnderlineOffset: 6,
    textDecorationThickness: 2,
    "&:hover": { opacity: 0.95 },
  },
  section5Title: {
    textAlign: "center",
    fontWeight: 900,
    fontSize: "clamp(2rem, 4.2vw, 3rem)",
    marginBottom: theme.spacing(6),
    color: "#fff",
  },
  reviewsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(4),
    alignItems: "start",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: theme.spacing(3),
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  reviewCard: {
    minWidth: 0,
    textAlign: "center",
    color: "#fff",
  },
  reviewStars: {
    width: "clamp(180px, 14vw, 240px)",
    height: "auto",
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: theme.spacing(1.5),
    filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.65))",
  },
  reviewName: {
    fontSize: 20,
    fontWeight: 900,
    marginBottom: theme.spacing(1),
    color: "#fff",
  },
  reviewText: {
    fontSize: 15.5,
    lineHeight: 1.5,
    opacity: 0.92,
    color: "#fff",
  },
  sectionBlack: {
    backgroundColor: "#000",
    backgroundImage: "url(/images/Black.png)",
    backgroundRepeat: "repeat",
    color: "#fff",
    padding: theme.spacing(6, 2),
  },
  sectionInner: {
    maxWidth: 1100,
    marginLeft: "auto",
    marginRight: "auto",
  },
  iconRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: theme.spacing(3),
    maxWidth: "min(1400px, 100%)",
    marginLeft: "auto",
    marginRight: "auto",
  },
  iconItem: {
    flex: "1 1 260px",
    maxWidth: 420,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  iconImg: {
    width: 280,
    height: 280,
    maxWidth: "75vw",
    objectFit: "contain",
    display: "block",
    marginBottom: theme.spacing(1),
  },
  iconCaption: {
    fontSize: "1.4rem",
    fontWeight: 900,
    lineHeight: 1.25,
  },
  iconCaptionSub: {
    marginTop: theme.spacing(0.5),
    fontSize: "1.2rem",
    fontWeight: 800,
    opacity: 0.95,
  },
  sectionTitle: {
    textAlign: "center",
    fontWeight: 800,
    fontSize: "clamp(1.9rem, 3.8vw, 2.6rem)",
    marginBottom: theme.spacing(1),
  },
  sectionSub: {
    textAlign: "center",
    opacity: 0.9,
    marginBottom: theme.spacing(4),
  },
  mediaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: theme.spacing(3),
    alignItems: "stretch",
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(2),
    },
  },
  mediaCard: {
    gridColumn: "span 6",
    backgroundColor: "rgba(0,0,0,0.6)",
    border: "1px solid rgba(0,123,255,0.55)",
    boxShadow: "0 10px 28px rgba(0,0,0,0.55)",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      gridColumn: "span 12",
    },
  },
  mediaCardImg: {
    width: "100%",
    height: 240,
    objectFit: "cover",
    display: "block",
    [theme.breakpoints.down("xs")]: {
      height: 210,
    },
  },
  mediaCardBody: {
    padding: theme.spacing(2, 2.25),
  },
  mediaCardTitle: {
    fontWeight: 800,
    marginBottom: theme.spacing(0.75),
  },
  sampleEpisodesSection: {
    backgroundColor: "#000",
    backgroundImage: "url(/images/Black.png)",
    backgroundRepeat: "repeat",
    color: "#fff",
    padding: theme.spacing(6, 2, 7),
  },
  sampleEpisodesTitle: {
    textAlign: "center",
    fontWeight: 800,
    fontSize: "clamp(2rem, 4.2vw, 3rem)",
    marginBottom: theme.spacing(1),
    color: "#fff",
  },
  sampleEpisodesSub: {
    textAlign: "center",
    fontSize: 18,
    lineHeight: 1.55,
    opacity: 0.92,
    marginBottom: theme.spacing(4),
    maxWidth: 640,
    marginLeft: "auto",
    marginRight: "auto",
    color: "#fff",
  },
  sampleEpisodesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2.5),
    maxWidth: 1200,
    marginLeft: "auto",
    marginRight: "auto",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sampleEpisodeCell: {
    minWidth: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    border: "1px solid rgba(255,255,255,0.12)",
    overflow: "hidden",
  },
  sampleEpisodeCaption: {
    padding: theme.spacing(1.25, 1.5),
    fontWeight: 700,
    fontSize: 15,
    lineHeight: 1.35,
    color: "#fff",
    backgroundColor: "#007BFF",
    borderBottom: "1px solid rgba(0,0,0,0.2)",
  },
  sampleEpisodePlayer: {
    width: "100%",
    minWidth: 0,
  },
  divider: {
    display: "block",
    width: "100%",
    maxWidth: 1100,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    height: 1,
    border: "none",
    backgroundColor: "rgba(255,255,255,0.28)",
  },
  subscribeBand: {
    backgroundColor: "#007BFF",
    color: "#fff",
    padding: theme.spacing(3, 2),
    textAlign: "center",
    fontWeight: 800,
    fontStyle: "italic",
    letterSpacing: "0.02em",
  },
}));

function RascalPodcast() {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const postHeroRef = useRef(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  useEffect(() => {
    const updateHeader = () => {
      const y =
        window.scrollY ??
        document.documentElement.scrollTop ??
        document.body.scrollTop ??
        0;
      setHeaderScrolled(y >= HEADER_SOLID_AFTER);
    };
    updateHeader();
    window.addEventListener("scroll", updateHeader, { passive: true });
    return () => window.removeEventListener("scroll", updateHeader);
  }, []);

  const scrollDown = () => {
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    const target = postHeroRef.current;
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({
        behavior: smooth ? "smooth" : "auto",
        block: "start",
      });
      return;
    }
    const root = document.scrollingElement ?? document.documentElement;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const maxScroll = Math.max(0, root.scrollHeight - root.clientHeight);
    root.scrollTop = Math.min(root.scrollTop + vh, maxScroll);
  };

  const featureIcons = useMemo(
    () => [
      {
        img: IMG("Icon - No Ads.png"),
        title: t("rascalPodcastFeature1Title"),
        sub: t("rascalPodcastFeature1Sub"),
      },
      {
        img: IMG("Icon - Story Driven.png"),
        title: t("rascalPodcastFeature2Title"),
        sub: t("rascalPodcastFeature2Sub"),
      },
      {
        img: IMG("Icon - High Impact.png"),
        title: t("rascalPodcastFeature3Title"),
        sub: t("rascalPodcastFeature3Sub"),
      },
    ],
    [t],
  );

  const sampleEpisodes = useMemo(() => {
    const ids =
      typeof i18n.language === "string" && i18n.language.startsWith("fr")
        ? SAMPLE_VIMEO_IDS_FR
        : SAMPLE_VIMEO_IDS_EN;
    return [1, 2, 3, 4].map((n, index) => ({
      video: ids[index],
      titleKey: `rascalPodcastSampleEpisode${n}Title`,
    }));
  }, [i18n.language]);

  return (
    <div className={classes.root}>
      <header
        className={clsx(classes.topNav, headerScrolled && classes.topNavSolid)}
      >
        <div className={classes.navBrand}>
          <img
            src={IMG("Rascal Podcast_White.png")}
            alt=""
            className={classes.navLogo}
            decoding="async"
          />
          <Typography
            className={classes.navTitle}
            variant="subtitle1"
            component="div"
          >
            {t("rascalPodcastNavTitle")}
          </Typography>
        </div>
      </header>
      <section className={classes.heroBleed}>
        <div className={classes.hero}>
          <div className={classes.heroMedia}>
            <img
              src={IMG("1.png")}
              alt=""
              className={classes.heroBgImage}
              decoding="async"
            />
            <div className={classes.heroOverlay} aria-hidden />
          </div>
          <div className={classes.heroUi}>
            <div className={classes.heroUiCenter}>
              <div className={classes.heroContent}>
                <img
                  src={IMG("Rascal Podcast_White - Big.png")}
                  alt={t("rascalPodcastHeroAlt")}
                  className={classes.heroMark}
                  decoding="async"
                />
                <Typography className={classes.heroSub} component="p">
                  {t("rascalPodcastHeroTagline")}
                </Typography>
                <MuiLink
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className={classes.heroCta}
                >
                  {t("rascalPodcastSubscribe")}
                </MuiLink>
              </div>
            </div>
            <div className={classes.heroUiBottom}>
              <IconButton
                type="button"
                className={classes.heroScrollBtn}
                aria-label={t("rascalPodcastScrollDown")}
                onClick={scrollDown}
                size="medium"
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </div>
          </div>
        </div>
      </section>
      <section className={classes.section2Intro}>
        <Typography className={classes.section2IntroText} component="p">
          {t("rascalPodcastIntro")}
        </Typography>
      </section>
      <section
        className={classes.section2Hero}
        aria-label={t("rascalPodcastWhatMakesDifferentAria")}
      >
        <div className={classes.section2HeroOverlay} aria-hidden />
        <div className={classes.section2HeroInner}>
          <Typography className={classes.section2Title} component="h2">
            {t("rascalPodcastWhatMakesDifferentTitle")}
          </Typography>
          <Box component="ul" className={classes.section2List}>
            <li>{t("rascalPodcastBullet1")}</li>
            <li>
              <strong>{t("rascalPodcastBullet2Strong")}</strong>
            </li>
            <li>{t("rascalPodcastBullet3")}</li>
            <li>
              <Trans i18nKey="rascalPodcastBullet4" components={[<strong />]} />
            </li>
            <li>{t("rascalPodcastBullet5")}</li>
            <li>
              <Trans i18nKey="rascalPodcastBullet6" components={[<strong />]} />
            </li>
            <li>{t("rascalPodcastBullet7")}</li>
          </Box>
          <div className={classes.section2CtaRow}>
            <MuiLink
              href="#"
              onClick={(e) => e.preventDefault()}
              className={classes.section2Cta}
            >
              {t("rascalPodcastSubscribe")}
            </MuiLink>
          </div>
        </div>
      </section>

      <section className={classes.section3Band}>
        {t("rascalPodcastWeeklyBand")}
      </section>

      <section className={classes.section3}>
        <div className={classes.sectionInner}>
          <Typography className={classes.section3Title} component="h2">
            {t("rascalPodcastLedByTitleLine1")}
            <br />
            {t("rascalPodcastLedByTitleLine2")}
          </Typography>
          <Typography className={classes.section3Sub} component="p">
            {t("rascalPodcastLedBySub")}
          </Typography>

          <div className={classes.hostGrid}>
            <div className={classes.hostCard}>
              <img
                src={IMG("3.jpg")}
                alt=""
                className={classes.hostImg}
                decoding="async"
              />
              <div className={classes.hostBody}>
                <Typography className={classes.hostBio} component="p">
                  <Trans
                    i18nKey="rascalPodcastChrisBio"
                    components={[<em />, <em />]}
                  />
                </Typography>
              </div>
            </div>

            <div className={classes.hostCard}>
              <img
                src={IMG("4.jpg")}
                alt=""
                className={classes.hostImg}
                decoding="async"
              />
              <div className={classes.hostBody}>
                <Typography className={classes.hostBio} component="p">
                  <Trans
                    i18nKey="rascalPodcastOrrinBio"
                    components={[<em />, <em />]}
                  />
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={classes.section4}>
        <div className={classes.sectionInner}>
          <div className={classes.sectionDivider} />
          <Typography className={classes.section4Title} component="h2">
            {t("rascalPodcastSpecialEpisodesTitle")}
          </Typography>
          <Typography className={classes.section4Sub} component="p">
            {t("rascalPodcastSpecialEpisodesSub")}
          </Typography>

          <div className={classes.hostGrid}>
            <div className={classes.hostCard}>
              <img
                src={IMG("5.jpg")}
                alt=""
                className={classes.hostImg}
                decoding="async"
              />
              <div className={classes.hostBody}>
                <Typography className={classes.section4Bio} component="p">
                  {t("rascalPodcastTerriBio")}
                </Typography>
              </div>
            </div>

            <div className={classes.hostCard}>
              <img
                src={IMG("6.jpg")}
                alt=""
                className={classes.hostImg}
                decoding="async"
              />
              <div className={classes.hostBody}>
                <Typography className={classes.section4Bio} component="p">
                  {t("rascalPodcastLaurieBio")}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section ref={postHeroRef} className={classes.sectionBlue}>
        <div className={classes.sectionInner}>
          <div className={classes.iconRow}>
            {featureIcons.map((f) => (
              <div key={f.title} className={classes.iconItem}>
                <img
                  src={f.img}
                  alt=""
                  className={classes.iconImg}
                  decoding="async"
                />
                <Typography className={classes.iconCaption} component="div">
                  {f.title}
                </Typography>
                <Typography className={classes.iconCaptionSub} component="div">
                  {f.sub}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={classes.sampleEpisodesSection} aria-labelledby="rascal-sample-episodes-heading">
        <div className={classes.sectionInner}>
          <Typography
            id="rascal-sample-episodes-heading"
            className={classes.sampleEpisodesTitle}
            component="h2"
          >
            {t("rascalPodcastSampleEpisodesTitle")}
          </Typography>
          <Typography className={classes.sampleEpisodesSub} component="p">
            {t("rascalPodcastSampleEpisodesSub")}
          </Typography>
          <div className={classes.sampleEpisodesGrid}>
            {sampleEpisodes.map((ep) => (
              <div key={ep.video} className={classes.sampleEpisodeCell}>
                <Typography
                  className={classes.sampleEpisodeCaption}
                  component="h3"
                  id={`rascal-sample-ep-label-${ep.video}`}
                >
                  {t(ep.titleKey)}
                </Typography>
                <div
                  className={classes.sampleEpisodePlayer}
                  aria-labelledby={`rascal-sample-ep-label-${ep.video}`}
                >
                  <Vimeo
                    id={`rascal-sample-vimeo-${ep.video}`}
                    responsive
                    style={{ width: "100%" }}
                    video={ep.video}
                    allow="fullscreen"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={classes.section5}>
        <div className={classes.sectionInner}>
          <div className={classes.section5Divider} />
          <div className={classes.section5Inner}>
            <Typography className={classes.section5Title} component="h2">
              {t("rascalPodcastReviewsTitle")}
            </Typography>

            <div className={classes.reviewsRow}>
              {[
                {
                  name: "Chris Craft",
                  text: t("rascalPodcastReview1Text"),
                },
                {
                  name: "Mandy Bergeron",
                  text: t("rascalPodcastReview2Text"),
                },
                {
                  name: "Matt Anderton",
                  text: t("rascalPodcastReview3Text"),
                },
                {
                  name: "Todd Ashworth",
                  text: t("rascalPodcastReview4Text"),
                },
              ].map((r) => (
                <div key={r.name} className={classes.reviewCard}>
                  <img
                    src={IMG("Stars.png")}
                    alt=""
                    className={classes.reviewStars}
                    decoding="async"
                  />
                  <Typography className={classes.reviewName} component="div">
                    {r.name}
                  </Typography>
                  <Typography className={classes.reviewText} component="div">
                    {r.text}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
          <div className={classes.section5Divider} />
        </div>
      </section>

      <section className={classes.section6}>
        <div className={classes.sectionInner}>
          <div className={classes.section6Inner}>
            <Typography className={classes.section6Text} component="div">
              {t("rascalPodcastClosing")}
            </Typography>
            <MuiLink
              href="#"
              onClick={(e) => e.preventDefault()}
              className={classes.section6Cta}
            >
              {t("rascalPodcastSubscribe")}
            </MuiLink>
          </div>
          <div className={classes.section6Divider} />
        </div>
      </section>
    </div>
  );
}

export default observer(RascalPodcast);
